import * as React from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";

import { cn } from "@src/lib/utils/cn";
import { cva, VariantProps } from "class-variance-authority";

// Track: 2px padding around the thumb, fully round, never changes size.
// Off: neutral 8% track (12% on hover / press) so the white thumb stays
// visible on white cards. On: brand, darker on hover / press. Disabled:
// muted, no hover. A `before:` pseudo-element widens the hit area 4px past
// the track on every side (6px on coarse pointers). Colour changes use a
// 150ms ease; the focus ring matches the pill Buttons.
const switchVariants = cva(
  [
    "peer group/switch relative inline-flex shrink-0 cursor-pointer items-center rounded-full p-0.5 outline-none",
    "before:absolute before:-inset-1 before:rounded-full pointer-coarse:before:-inset-1.5",
    "transition-[color,background-color,border-color,box-shadow] duration-150 ease-[cubic-bezier(0.4,0,0.2,1)]",
    "focus-visible:ring-[3px] focus-visible:ring-brand-border disabled:cursor-not-allowed",
    "data-[state=unchecked]:bg-action-secondary-hover enabled:hover:data-[state=unchecked]:bg-action-secondary-active enabled:active:data-[state=unchecked]:bg-action-secondary-active",
    "data-[state=checked]:bg-action-primary enabled:hover:data-[state=checked]:bg-action-primary-hover enabled:active:data-[state=checked]:bg-action-primary-active",
    "disabled:data-[state=checked]:bg-icon-disabled disabled:data-[state=unchecked]:bg-action-secondary",
  ],
  {
    variants: {
      size: {
        sm: "h-4 w-6",
        lg: "h-7 w-11",
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
);

// Thumb slide and press stretch share one spring-like easing: a critically
// damped spring, x(t) = 1 - (1 + wt)e^(-wt) with w = 28.7 rad/s (stiffness
// ~820 at mass 1, or ~550 at mass 0.67), sampled every 15ms over 300ms and
// normalised to end at 1. It reaches 70% by 85ms, settles by 300ms and never
// overshoots. No slide when the user prefers reduced motion.
//
// While the pointer is down the thumb grows 4px wider. Checked, it also
// moves 4px less, so it grows leftward and stays flush with the right end.
const thumbVariants = cva(
  [
    "pointer-events-none block rounded-full bg-action-primary-foreground shadow-default ring-0",
    "transition-[translate,width] duration-300 ease-[linear(0,0.07,0.214,0.371,0.514,0.635,0.731,0.804,0.86,0.9,0.93,0.951,0.967,0.977,0.985,0.99,0.994,0.996,0.998,0.999,1)] motion-reduce:transition-none",
    "data-[state=unchecked]:translate-x-0",
  ],
  {
    variants: {
      size: {
        sm: "h-3 w-3 group-enabled/switch:group-active/switch:w-4 data-[state=checked]:translate-x-2 group-enabled/switch:group-active/switch:data-[state=checked]:translate-x-1",
        lg: "h-6 w-6 group-enabled/switch:group-active/switch:w-7 data-[state=checked]:translate-x-4 group-enabled/switch:group-active/switch:data-[state=checked]:translate-x-3",
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
);

export interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
    VariantProps<typeof switchVariants> {}

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(({ className, size, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(switchVariants({ size, className }))}
    ref={ref}
    {...props}
  >
    <SwitchPrimitives.Thumb className={cn(thumbVariants({ size }))} />
  </SwitchPrimitives.Root>
));
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch, switchVariants, thumbVariants };
